import { useIsMutating } from "@tanstack/react-query";
import { ColumnHelper } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { createCustomColumn } from "PFApp/activities/manage_roles/parts/suggested_wm_side_panel/suggested_shortlists_table/custom_column";
import { useProfilePillSectionCustomTypes } from "PFApp/hooks";
import { Alert } from "PFComponents/alert";
import { Flex, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import Table from "PFComponents/table";
import { mutationKeys } from "PFCore/hooks/queries/shortlists/mutation_keys";
import { ShortlistState } from "PFTypes";
import { useTranslation } from "react-i18next";

import { createActionColumn } from "./action_column";
import { createAvailabilityColumn } from "./availability_column";
import { createMatchColumn } from "./match_column";
import { createShortlistStateColumn } from "./shortlist_state_column";
import css from "./suggested_shortlists_table.module.scss";
import { createSuggestedWmColumn } from "./suggested_wm_column";
import { isOpenVacancyItem, SuggestedWmTableItem } from "./types";

type SuggestedShortlistsTableProps = {
  tableData: SuggestedWmTableItem[];
  isLoading: boolean;
  isFetching: boolean;
};

export const SuggestedShortlistsTable = ({
  tableData,
  isLoading,
  isFetching
}: SuggestedShortlistsTableProps) => {
  const { t } = useTranslation("activities");
  const { profilePillCustomTypes, getValue } = useProfilePillSectionCustomTypes();

  const isCreatePending = useIsMutating({ mutationKey: mutationKeys.create() });
  const isUpdatePending = useIsMutating({ mutationKey: mutationKeys.update() });
  const isMutationPending = isCreatePending + isUpdatePending > 0;

  if (isLoading) {
    return <LoadingDots circlesEnabled centered />;
  }

  const openVacancyItemsCount = tableData.filter(isOpenVacancyItem).length;
  const isTableEmpty = isEmpty(tableData) && openVacancyItemsCount === 0;

  const columnsGenerator = (columnHelper: ColumnHelper<SuggestedWmTableItem>) => [
    createSuggestedWmColumn(columnHelper, t("manageRoles.suggestedWmSidePanel.columns.suggestedWm")),
    ...profilePillCustomTypes.map((customType) =>
      createCustomColumn(columnHelper, customType, (profile) => getValue(profile.pills, customType.id))
    ),
    createMatchColumn(columnHelper, t("manageRoles.suggestedWmSidePanel.columns.match")),
    createAvailabilityColumn(columnHelper, t("manageRoles.suggestedWmSidePanel.columns.availability")),
    createShortlistStateColumn(
      columnHelper,
      t("manageRoles.suggestedWmSidePanel.columns.shortlistState"),
      (state: ShortlistState) => t(`show.shortlist.state.${state}`)
    ),
    createActionColumn(columnHelper)
  ];

  return (
    <Stack className={css.root}>
      {(isFetching || isMutationPending) && (
        <Flex className={css.loading} center>
          <LoadingDots circlesEnabled circleSize="xxxxs" />
        </Flex>
      )}
      <Alert
        kind="general"
        title={t("manageRoles.suggestedWmSidePanel.openVacanciesInfo", { count: openVacancyItemsCount })}
      />
      {!isTableEmpty && (
        <Table<SuggestedWmTableItem>
          tableData={tableData}
          generateColumns={columnsGenerator}
          disableAutoResizing
        />
      )}
    </Stack>
  );
};
